import React, {FC, useCallback, useContext, useState} from "react";
import {IconButton, MaskedTextField, Stack} from "@fluentui/react";
import {useTranslation} from "react-i18next";
import {hr} from "../locales/hr";
import {GetApiError} from "./ShowApiError";
import {IModelError} from "../interfaces/IModelError";
import {LanguageContext} from "../contexts/LanguageContext";
import {OnixCallout} from "./OnixCallout";
import {DropdownFromList} from "./DropdownFromList";

export interface ILocalTimeField {
    label?: keyof typeof hr;
    date?: Date;
    onValidChange?: (d: Date) => void;
    styles?: any;
    disabled?: boolean;
    title?: string;
    onKeyDownCapture?: (event: React.KeyboardEvent<HTMLElement>) => void;
    errorMessage?: string;
    utcOnly?: boolean;
    lowerLimit?: number;
    upperLimit?: number;
}

export const LocalTimeField: FC<ILocalTimeField> = ({
                                                        utcOnly,
                                                        label,
                                                        title,
                                                        disabled,
                                                        errorMessage,
                                                        onKeyDownCapture,
                                                        date,
                                                        onValidChange,
                                                        styles,
                                                        lowerLimit,
                                                        upperLimit,
                                                    }) => {
    const {t} = useTranslation();
    const [error, setError] = useState<IModelError<{ date: Date }>>();
    const {moment} = useContext(LanguageContext);

    const timeChanged = useCallback(
        (
            event?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
            newValue?: string | undefined,
        ) => {
            if (date) {
                const originalDateTime = utcOnly
                    ? moment.utc(date)
                    : moment.utc(date).local();
                if (newValue) {
                    const [stringHours, stringMinutes] = newValue
                        .split(":")
                        .map((m: string) => m.replace("_", ""));
                    if (
                        stringHours.trim().length === 2 &&
                        stringMinutes.trim().length === 2
                    ) {
                        if (stringHours && stringMinutes) {
                            const hours = Number.parseInt(stringHours);
                            const minutes = Number.parseInt(stringMinutes);
                            if (!isNaN(hours) && !isNaN(minutes)) {
                                if (hours > -1 && hours < 24 && minutes > -1 && minutes < 60) {
                                    originalDateTime.set({hours, minutes});
                                    onValidChange?.(
                                        utcOnly
                                            ? moment(originalDateTime).toDate()
                                            : moment(originalDateTime).local().toDate(),
                                    );
                                    setError(undefined);
                                    return;
                                }
                            }
                        }
                    }
                }
            }
            setError({date: [t("enter-a-valid-time")]});
        },
        [date, onValidChange, t, utcOnly],
    );

    return (
        <Stack styles={styles}>
            <OnixCallout
                paddingLess
                title="time"
                trigger={(
                    id: string,
                    show: (
                        e: React.MouseEvent<any, any> | undefined,
                        data: any,
                        forceStatus?: boolean | undefined,
                        renderData?: any,
                    ) => void,
                ): JSX.Element => (
                    <MaskedTextField
                        label={label ? t(label) : undefined}
                        disabled={disabled}
                        title={title}
                        errorMessage={GetApiError(error, "date") ?? errorMessage}
                        onKeyDownCapture={onKeyDownCapture}
                        //  value={moment.utc(date).local().format("HH:mm")}
                        value={(utcOnly
                                ? moment.utc(date)
                                : moment.utc(date).local()
                        ).format("HH:mm")}
                        mask={"99:99"}
                        onChange={timeChanged}
                        styles={{suffix: {padding: 0, margin: 0}}}
                        onRenderSuffix={() => (
                            <IconButton
                                id={id}
                                iconProps={{
                                    iconName: "Clock",
                                    style: {color: "rgb(133,133,133)"},
                                }}
                                onClick={(event) => {
                                    show(event, undefined);
                                }}
                            />
                        )}
                    />
                )}
            >
                {(realign, show) => (
                    <div>
                        <DropdownFromList
                            // list of times in 30 minute intervals
                            list={[...Array(48)]
                                .map((_, i) => {
                                    const hours = Math.floor(i / 2);
                                    const minutes = (i % 2) * 30;
                                    const momentTime = moment.utc().set({hours, minutes});
                                    return {
                                        key: momentTime.format("HH:mm"),
                                        text: momentTime.format("HH:mm"),
                                    };
                                })
                                .filter((m) => {
                                    let passes = true;
                                    if (lowerLimit) {
                                        passes =
                                            passes &&
                                            moment(m.key, "HH:mm").isSameOrAfter(
                                                moment(lowerLimit, "HH:mm"),
                                            );
                                    }

                                    if (upperLimit) {
                                        passes =
                                            passes &&
                                            moment(m.key, "HH:mm").isSameOrBefore(
                                                moment(upperLimit, "HH:mm"),
                                            );
                                    }

                                    return passes;
                                })}
                            toKND={(m) => ({data: m, key: m.key, name: m.text})}
                            value={moment.utc(date).local().format("HH:mm")}
                            inline
                            setValue={(v) => {
                                timeChanged(undefined, v?.key);
                                show();
                            }}
                        />
                    </div>
                )}
            </OnixCallout>
        </Stack>
    );
};
